// components/Survey/SurveyForm.jsx (Updated)
import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { companyService } from '../../services/companyService';

const SurveyForm = ({ onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    companyId: '',
    startDate: '',
    endDate: '',
    isActive: true
  });

  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    if (isEditing && initialData) {
      // NOTE: the API returns the company as a nested `company: { id, name }`
      // object, not a flat `companyId`. Previously this only checked
      // `initialData.companyId` (which is always undefined), so the Company
      // dropdown silently reset to blank every time an existing survey was
      // edited. Falling back to `company?.id` restores the correct value.
      const resolvedCompanyId =
        initialData.companyId ?? initialData.company?.id ?? '';

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        companyId: resolvedCompanyId !== '' ? resolvedCompanyId.toString() : '',
        startDate: initialData.startDate ? initialData.startDate.split('T')[0] : '',
        endDate: initialData.endDate ? initialData.endDate.split('T')[0] : '',
        isActive: initialData.isActive ?? true
      });
    }
    loadCompanies();
  }, [initialData, isEditing]);

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const data = await companyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      setErrors({ general: 'Failed to load companies' });
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Survey name is required';
    if (!formData.companyId) newErrors.companyId = 'Company is required';
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Maps ASP.NET Core's PascalCase validation-error keys back onto this
  // form's field names so a failed save highlights the right input instead
  // of only showing a generic banner.
  const FIELD_ERROR_MAP = {
    name: 'name',
    description: 'description',
    companyid: 'companyId',
    startdate: 'startDate',
    enddate: 'endDate',
    isactive: 'isActive'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const submitData = {
        // The update endpoint expects the survey's id in the body (in
        // addition to the route parameter); omitting it was causing the
        // API's model validation to reject the whole update with a generic
        // "One or more validation errors occurred." response.
        ...(isEditing && initialData?.id ? { id: initialData.id } : {}),
        ...formData,
        companyId: parseInt(formData.companyId),
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      };
      await onSubmit(submitData);
    } catch (error) {
      if (error.fieldErrors) {
        const mapped = {};
        Object.entries(error.fieldErrors).forEach(([key, msgs]) => {
          const fieldName = FIELD_ERROR_MAP[key.toLowerCase()];
          const msg = Array.isArray(msgs) ? msgs.join(' ') : msgs;
          if (fieldName) mapped[fieldName] = msg;
        });
        setErrors({ ...mapped, general: error.message });
      } else {
        setErrors({ general: error.message || 'Error saving survey' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-orange-100 via-white to-orange-100 rounded-2xl shadow-md p-6 border border-orange-100 h-fit">
        <div className="w-48 h-48 bg-gradient-to-br from-orange-200 to-orange-300 rounded-2xl flex items-center justify-center mb-3 mx-auto">
          <DocumentTextIcon className="w-16 h-16 text-orange-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-800">Survey Settings</h2>
        <p className="text-gray-500 text-sm mt-1 leading-snug">
          {isEditing 
            ? 'Update survey details, dates, and visibility settings.' 
            : 'Create a new survey with basic information and scheduling.'}
        </p>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-xl shadow-sm p-6 border border-orange-100"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Survey Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              required
              placeholder="Employee Satisfaction Survey 2024"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 transition-colors ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              rows={3}
              placeholder="Brief description of the survey purpose and scope..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company *
            </label>
            <select
              name="companyId"
              value={formData.companyId || ''}
              onChange={handleChange}
              required
              disabled={loadingCompanies}
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm bg-white ${
                errors.companyId ? 'border-red-500' : ''
              } ${loadingCompanies ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="">Select a company</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
            {errors.companyId && (
              <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>
            )}
            {loadingCompanies && (
              <p className="text-gray-500 text-sm mt-1">Loading companies...</p>
            )}
          </div>
        </div>

        {/* Schedule Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Schedule Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate || ''}
                onChange={handleChange}
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 ${
                  errors.startDate ? 'border-red-500' : ''
                }`}
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Leave empty to start immediately
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate || ''}
                onChange={handleChange}
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 ${
                  errors.endDate ? 'border-red-500' : ''
                }`}
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Leave empty for no end date
              </p>
            </div>
          </div>
        </div>

        {/* Status Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Status Settings
          </h3>
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-5 h-5 text-orange-600 rounded border-gray-300 focus:ring-orange-400"
            />
            <div>
              <label className="text-sm font-medium text-gray-700">
                Active Survey
              </label>
              <p className="text-gray-500 text-xs mt-1">
                {formData.isActive 
                  ? 'Survey is currently active and accepting responses' 
                  : 'Survey is inactive and not accepting responses'}
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEditing ? 'Update Survey' : 'Save Survey'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default SurveyForm;